import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

// ── Types (mirror backend) ─────────────────────────────

export interface ParameterSet {
  ID: number;
  STEP: number;
  SavedName: string;
  Schools: string | null;
  AccountStatuses: string;
  AccountConditions: string;
  StudentStatuses: string;
  NoPaymentsSinceBuffer: number;
  WithdrawnBeforeBuffer: number;
  ContractDateBuffer: number;
  CreditBalanceOnBuffer: number;
  MinimumCurrentBalance: number;
  MinimumPayoffBalance: number;
  MaximumPayoffBalance: number;
  Deferred: boolean;
  IsMonthly: boolean;
  IsWeekly: boolean;
  IsBiWeekly: boolean;
  IsActive: boolean;
  CreatedDate: string;
  ModifiedDate: string | null;
}

export interface ParameterSetInput {
  Step?: number;
  SavedName: string;
  Schools: string | null;
  AccountStatuses: string;
  AccountConditions: string;
  StudentStatuses: string;
  NoPaymentsSinceBuffer: number;
  WithdrawnBeforeBuffer: number;
  ContractDateBuffer: number;
  CreditBalanceOnBuffer: number;
  MinimumCurrentBalance: number;
  MinimumPayoffBalance: number;
  MaximumPayoffBalance: number;
  Deferred: boolean;
  IsMonthly: boolean;
  IsWeekly: boolean;
  IsBiWeekly: boolean;
}

export interface ProcessRun {
  RunID: number;
  RunDate: string;
  RunType: string;
  ScheduleType: string;
  StepsToExecute: string;
  Status: string;
  TotalAccountsFound: number;
  TotalAccountsAssigned: number;
  TotalPayoffBalance: number;
  StartTime: string;
  EndTime: string | null;
  DurationSeconds: number | null;
  ErrorMessage: string | null;
}

export interface StepResult {
  StepResultID: number;
  RunID: number;
  StepNumber: number;
  StepName: string;
  Status: string;
  AccountCount: number;
  TotalCurrentBalance: number;
  TotalPayoffBalance: number;
  AssignedCount: number;
  DurationSeconds: number | null;
  ErrorMessage: string | null;
}

export interface AccountResult {
  AccountResultID: number;
  AccountNumber: string;
  ContractDate: string | null;
  AccountStatus: string | null;
  AccountCondition: string | null;
  StudentStatus: string | null;
  CurrentBalance: number | null;
  PayoffBalance: number | null;
  LastPaymentDate: string | null;
  SchoolName: string | null;
  StudentName: string | null;
  SignerName: string | null;
  WasAssigned: boolean;
}

export interface AssignmentHistory {
  AssignmentID: number;
  AccountNumber: string;
  CollectionAgencyName: string | null;
  AssignmentDate: string;
  PayoffBalanceAtAssignment: number | null;
  StepNumber: number;
  StepName: string | null;
  Status: string;
  StudentName: string | null;
  SchoolName: string | null;
}

export interface LookupItem {
  ID: number;
  Name: string;
}

// ── Dashboard Types ─────────────────────────────────────

export interface DashboardMonthData {
  year: number;
  month: number;
  monthName: string;
  runType: string;
  accountCount: number;
  payoffBalance: number;
  assignedCount: number;
}

export interface DashboardStepSummary {
  stepNumber: number;
  stepName: string;
  months: DashboardMonthData[];
}

export interface DashboardData {
  stepSummaries: DashboardStepSummary[];
  recentRuns: ProcessRun[];
}

// ── Dashboard Hook ──────────────────────────────────────

export function useDashboardData() {
  return useQuery({
    queryKey: ['caap', 'dashboard'],
    queryFn: async () => {
      const { data } = await api.get<DashboardData>('/caap/dashboard');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ── Parameter Set Hooks ─────────────────────────────────

export function useParameterSets() {
  return useQuery({
    queryKey: ['caap', 'parameters'],
    queryFn: async () => {
      const { data } = await api.get<ParameterSet[]>('/caap/parameters');
      return data;
    },
  });
}

export function useUpdateParameterSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: number; input: ParameterSetInput }) => {
      const { data } = await api.put<ParameterSet>(`/caap/parameters/${id}`, input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['caap', 'parameters'] }),
  });
}

export function useCreateParameterSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ParameterSetInput) => {
      const { data } = await api.post<ParameterSet>('/caap/parameters', input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['caap', 'parameters'] }),
  });
}

export function useDeleteParameterSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/caap/parameters/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['caap', 'parameters'] }),
  });
}

// ── Run Hooks ───────────────────────────────────────────

export function useProcessRuns() {
  return useQuery({
    queryKey: ['caap', 'runs'],
    queryFn: async () => {
      const { data } = await api.get<ProcessRun[]>('/caap/runs');
      return data;
    },
  });
}

export function useRunDetails(runId: number) {
  return useQuery({
    queryKey: ['caap', 'runs', runId],
    queryFn: async () => {
      const { data } = await api.get<{ run: ProcessRun; steps: StepResult[] }>(`/caap/runs/${runId}`);
      return data;
    },
    enabled: runId > 0,
    refetchInterval: (query) => {
      const status = query.state.data?.run?.Status;
      return status === 'Running' ? 3000 : false;
    },
  });
}

export function useAccountResults(runId: number, stepNumber: number) {
  return useQuery({
    queryKey: ['caap', 'runs', runId, 'steps', stepNumber, 'accounts'],
    queryFn: async () => {
      const { data } = await api.get<AccountResult[]>(`/caap/runs/${runId}/steps/${stepNumber}/accounts`);
      return data;
    },
    enabled: runId > 0 && stepNumber > 0,
  });
}

export function useAssignmentHistory(runId: number) {
  return useQuery({
    queryKey: ['caap', 'runs', runId, 'assignments'],
    queryFn: async () => {
      const { data } = await api.get<AssignmentHistory[]>(`/caap/runs/${runId}/assignments`);
      return data;
    },
    enabled: runId > 0,
  });
}

// ── Lookup Hooks ────────────────────────────────────────

export function useLookups() {
  const schools = useQuery({
    queryKey: ['caap', 'lookups', 'schools'],
    queryFn: async () => {
      const { data } = await api.get<LookupItem[]>('/caap/lookups/schools');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const accountStatuses = useQuery({
    queryKey: ['caap', 'lookups', 'account-statuses'],
    queryFn: async () => {
      const { data } = await api.get<LookupItem[]>('/caap/lookups/account-statuses');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const accountConditions = useQuery({
    queryKey: ['caap', 'lookups', 'account-conditions'],
    queryFn: async () => {
      const { data } = await api.get<LookupItem[]>('/caap/lookups/account-conditions');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const studentStatuses = useQuery({
    queryKey: ['caap', 'lookups', 'student-statuses'],
    queryFn: async () => {
      const { data } = await api.get<LookupItem[]>('/caap/lookups/student-statuses');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  return { schools, accountStatuses, accountConditions, studentStatuses };
}

// ── Start Run Hook ─────────────────────────────────────

export interface StartRunInput {
  maxStepNumber: 6 | 7;
  isExecuteMode: boolean;
  collectionAgencyId?: number;
  effectiveDate?: string;
}

export function useStartRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: StartRunInput) => {
      const { data } = await api.post('/caap/runs', input, { timeout: 300000 });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['caap', 'runs'] });
      qc.invalidateQueries({ queryKey: ['caap', 'dashboard'] });
    },
  });
}

// ── Collection Agencies Lookup ─────────────────────────

export function useCollectionAgencies() {
  return useQuery({
    queryKey: ['caap', 'lookups', 'collection-agencies'],
    queryFn: async () => {
      const { data } = await api.get<LookupItem[]>('/caap/lookups/collection-agencies');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ── Connection Info Hook ───────────────────────────────

export interface ConnectionInfo {
  server: string;
  database: string;
  status: string;
}

export function useConnectionInfo() {
  return useQuery({
    queryKey: ['caap', 'settings', 'connection-info'],
    queryFn: async () => {
      const { data } = await api.get<ConnectionInfo>('/caap/settings/connection-info');
      return data;
    },
    staleTime: 30 * 1000,
  });
}

// ── Download Helpers ────────────────────────────────────

// Authenticated downloads — the axios interceptor adds the bearer token, then we
// turn the blob response into a temporary object URL and trigger the browser save.
async function downloadBlob(url: string, fallbackFilename: string): Promise<void> {
  const response = await api.get<Blob>(url, { responseType: 'blob' });

  const filename = parseContentDispositionFilename(
    response.headers['content-disposition'] as string | undefined
  ) ?? fallbackFilename;

  const objectUrl = window.URL.createObjectURL(response.data);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(objectUrl);
}

function parseContentDispositionFilename(header: string | undefined): string | null {
  if (!header) return null;
  // Prefer RFC 5987 `filename*=UTF-8''…` if present, fall back to plain `filename=`.
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8Match) return decodeURIComponent(utf8Match[1]);
  const plainMatch = /filename="?([^";]+)"?/i.exec(header);
  return plainMatch ? plainMatch[1] : null;
}

export function downloadExcelExport(runId: number): Promise<void> {
  return downloadBlob(`/caap/runs/${runId}/export`, `CAAP_Run_${runId}.xlsx`);
}

export function downloadCollectionFile(runId: number): Promise<void> {
  const today = new Date().toISOString().slice(0, 10);
  return downloadBlob(
    `/caap/runs/${runId}/collection-file`,
    `CollectionAgency_Assignment_${today}.txt`
  );
}
